/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { LoadingOutlined, UnorderedListOutlined } from '@ant-design/icons';

import { Job } from '@/client';
import { AvatarAndName } from '@/components/AvatarAndName';
import { PageHeader } from '@/components/PageHeader';
import { RelativeTime } from '@/components/RelativeTime';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import './JobDetailsHeader.css';

export type JobDetailsHeaderProps = {
  job: Job;
};

export function JobDetailsHeader({ job }: JobDetailsHeaderProps) {
  const { t } = useMimicTranslation('jobs');
  return (
    <PageHeader
      icon={<UnorderedListOutlined style={{ width: '18px', height: '18px' }} />}
      title={
        <Text type="subtle" size="md" strong style={{ lineHeight: '32px' }}>
          {t('jobDetailsTitle')}
        </Text>
      }
      text={
        <div className="job-details-header">
          <div className="job-details-header-title">
            <Title data-testid="job-details-title" level={2} style={{ fontSize: '23px' }}>
              {job.name}
            </Title>
            {job.status === 'running' && (
              <LoadingOutlined style={{ fontSize: '20px' }} data-testid="job-running-icon" />
            )}
          </div>
          <div className="job-details-container">
            <AvatarAndName userInfo={job.createdBy} />
            <div className="job-details-start">
              <Text size="md" type="subtle" strong>
                {t('start')}
              </Text>
              <Text size="md" type="subtle" data-testid="job-date-created">
                <RelativeTime date={job.dateCreated} /> UTC
              </Text>
            </div>
            <div className="job-details-end">
              <Text size="md" type="subtle" strong>
                {t('end')}
              </Text>
              <Text size="md" type="subtle" data-testid="job-date-completed">
                {job.dateCompleted && (
                  <>
                    <RelativeTime date={job.dateCompleted} /> UTC
                  </>
                )}
                {!job.dateCompleted && '-'}
              </Text>
            </div>
            <div className="job-details-id">
              <Text size="md" type="subtle" mono copyable>
                {job.id}
              </Text>
            </div>
          </div>
        </div>
      }
      level={4}
      fontWeight="300"
    />
  );
}
